import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCRefModalProps, RCCHookToast, RCCServicesApi, RCCSwitch, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import LovUsers from "src/components/Lov/Users";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";

import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IModalChangeResponsibleOrAuthorityRef } from "./interface";
import changeResponsibleOrAuthoritySchema from "./schemas";

const ModalChangeResponsibleOrAuthority: React.ForwardRefRenderFunction<IModalChangeResponsibleOrAuthorityRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const modalChangeResponsibleOrAuthorityRef = useRef<RCCRefModalProps>(null);

	const [subtitle, setSubtitle] = useState("");
	const [selectedUser, setSelectedUser] = useState<number | undefined>(undefined);

	const { 
		control,
		reset,
		register,
		handleSubmit,
		formState: { errors } ,
		clearErrors,
		setValue,
		setError: setErrorModify,
		watch
	} = useForm();


	const openModal = (efa005Id: number, efa005Nome: string) => {
		reset();
		setSelectedUser(efa005Id);
		setValue("type", "R");
		setSubtitle(`${translate("Usuário")}: ${efa005Nome}`);
		modalChangeResponsibleOrAuthorityRef.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));

	const modules = [
		{
			value: "projects",
			label: translate("Projetos")
		},{
			value: "indicators",
			label: translate("Indicadores")
		},
		{
			value: "risks",
			label: translate("Riscos")
		},
		{
			value: "actions",
			label: translate("Ações")
		},
		{
			value: "kanban",
			label: translate("Kanbans")
		},
		{
			value: "occurrences",
			label: translate("Ocorrências")
		},
		{
			value: "meetings",
			label: translate("Reuniões")
		},
		{
			value: "okr",
			label: "OKR's"
		},
		{
			value: "programs",
			label: translate("Programas")
		},
		{
			value: "audits",
			label: translate("Auditorias")
		}
	];

	const submit = async (data: any) => {
		

		const returnBool = (checkBox: any) => {
			if(!checkBox)
				return false;
			if(typeof checkBox === "boolean")
				return checkBox;
			if(typeof checkBox === "string"){
				if(checkBox === "true")
					return true;
				return false;
			}
			return false;
		};

		try{
			clearErrors();	
			modalChangeResponsibleOrAuthorityRef.current?.setLoadingButton();
			const dataChanged = data;
			dataChanged.actions = returnBool(data.actions);
			dataChanged.indicators = returnBool(data.indicators);
			dataChanged.meetings = returnBool(data.meetings);
			dataChanged.occurrences = returnBool(data.occurrences);
			dataChanged.projects = returnBool(data.projects);
			dataChanged.risks = returnBool(data.risks);
			dataChanged.kanban = returnBool(data.kanban);
			dataChanged.okr = returnBool(data.okr);
			dataChanged.programs = returnBool(data.programs);
			dataChanged.audits = returnBool(data.audits);

			await changeResponsibleOrAuthoritySchema(translate).validate(data, {
				abortEarly: false
			});

			if(String(selectedUser) === data.newUser){
				addToast({
					color: "yellow",
					description: translate("Por favor, selecione um usuário diferente do atual"),
					title: translate("Atenção")
				});
				modalChangeResponsibleOrAuthorityRef.current?.resetLoadingButton();
				return;
			}

			await RCCServicesApi.postRequest({
				method: "User/ChangeResponsibleOrAuthority",
				urlParameters: {
					...data,
					oldUser: selectedUser
				}
			});

			const messageSuccess = (type: any) => {
				if(type === "B")
					return translate("Responsáveis e autoridades alterados com sucesso");
				if(type === "R")
					return translate("Responsáveis alterados com sucesso");
				return translate("Autoridades alteradas com sucesso");
			}; 
			
			addToast({
				color: "green",
				description: messageSuccess(dataChanged?.type),
				title: translate("Sucesso")
			});

			modalChangeResponsibleOrAuthorityRef.current?.resetLoadingButton();
			modalChangeResponsibleOrAuthorityRef.current?.closeModal();

		}catch(err){
			modalChangeResponsibleOrAuthorityRef.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorModify)){
				return;
			}
			
			const errorSuccess = (type: any) => {
				if(type === "B")
					return translate("Erro ao alterar responsáveis e autoridades");
				if(type === "R")
					return translate("Erro ao alterar responsáveis");
				return translate("Erro ao alterar autoridades");
			}; 

			standardErrorMessages(err, errorSuccess(data?.type), addToast);
		}

	};

	const type = watch("type");

	const checkIfIsDisabled = (field: string) => {
		if(type === "A"){
			if(field === "meetings" || field === "risks" || field === "indicators")
				return true;
			return false;
		}
		return false;
	};

	const selectAll = () => {
		modules.forEach((e) => {
			setValue(e.value, true);
		});
	};

	const deselectAll = () => {
		modules.forEach((e) => {
			setValue(e.value, false);
		});
	};

	return (
		<Modal
			ref={modalChangeResponsibleOrAuthorityRef}
			header={{
				title: translate("Alterar Responsável/Autoridade"),
				subtitle,
				icon: {
					name:  "exchange"
				}
			}}
			styleInline={ModalSide}
			onSubmit={handleSubmit(submit)}
		>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
			>
				<RCCGrid
					container
					item
					xs={12}
					sm={10}
				>
					<RCCSelectOptionsCard
						name="type"
						control={control}
						options={[
							{
								value: "R",
								label:translate("Responsável")
							},
							{
								value: "A",
								label:translate("Autoridade")
							},
							{
								value: "B",
								label:translate("Ambos")
							}
						]}
						label=""
						defaultValue="R"
					/>

					<LovUsers 
						control={control}
						name="newUser"
						label={translate("Novo usuário")}
						error={errors.newUser}
						required
					/>
							
					<RCCGrid
						container
						item
						justifyContent="flex-start"
						style={{
							padding: "10px 0"
						}}
					>

						<RCCSwitch 	
							name="toggleAll" 
							register={register} 
							label={translate("Marcar todos")}
							labelPosition="end"
							onChangeCapture={(e: any) => {
								if(e.target.checked)
									selectAll();
								else
									deselectAll();
							}}
						/>

						<hr />
					</RCCGrid>

					<RCCGrid
						container
						item
						style={{
							flexDirection: "column"
						}}
					>
						{modules.map((module) => (
							<RCCGrid 
								container
								item
								key={v4()} 
								style={{ 
									display: checkIfIsDisabled(module.value) ? "none": "block"
								}}
							>
								<RCCSwitch 	
									name={`${module.value}`} 
									register={register} 
									label={module.label}
									labelPosition="end"
								/>
							</RCCGrid>	
						))}
					</RCCGrid>
				
				</RCCGrid>
			</RCCGrid>
		</Modal>
	);
};

export default React.forwardRef(ModalChangeResponsibleOrAuthority);

